import '../../styles/_cle-dialog.scss';

import React, {
    CSSProperties,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

import { CleTimeStamp } from '../../datasource/generated';
import { cleTimestampSaveLock, useSaveCleTimeStampsMutation } from '../../datasource/mutations/cleTimeStamps';
import { useGetCleDialogMessagesQuery } from '../../datasource/queries/cmsMessages';
import { ScreenSizeQueries } from '../../enums/ScreenSizeQueries';
import { useAppDispatch, useAppSelector, useAppStore } from '../../hooks';
import { MediaLogEventTypes, useLogMediaEvents } from '../../hooks/useLogMediaEvents';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { usePlayerElement } from '../../hooks/usePlayerElement';
import { getErrorMessage } from '../../utility/errorUtils';
import Button from '../Button/Button';
import { clearTimeStampEvent, setFailedTimeStamps } from '../Store/cleSlice';
import { Toggle } from '../Toggle/Toggle';

export interface ICleDialogProps {
    container?: RefObject<HTMLElement>;
    chime: RefObject<HTMLAudioElement>;
    animateCreditIcon: () => void;
}

type CleProgressStyle = CSSProperties & {
    '--cle-dialog-progress-duration'?: string;
};

type TimeoutHandle = ReturnType<typeof setTimeout>;

const acknowledgementDisplayMilliseconds = 1600;
const exitTransitionMilliseconds = 500;

export const calculateAttendanceProgress = (
    startTimestampMilliseconds: number | undefined,
    currentTimestampMilliseconds: number,
    durationSeconds: number | undefined,
) => {
    if (startTimestampMilliseconds === undefined || !durationSeconds || durationSeconds <= 0) return 0;

    const elapsedMilliseconds = Math.max(currentTimestampMilliseconds - startTimestampMilliseconds, 0);
    return Math.min(elapsedMilliseconds / (durationSeconds * 1000), 1);
};

export const CleDialog = ({
    container,
    chime,
    animateCreditIcon,
} : ICleDialogProps) => {

    const dispatch = useAppDispatch();
    const store = useAppStore();
    const sessionGuid = useAppSelector((state) => state.cle.sessionGuid);
    const timeStampEvent = useAppSelector((state) => state.cle.timeStampEvent);
    const configuredCountdownSeconds = useAppSelector((state) => state.clientConfig?.attendanceVerificationCountdownSeconds);
    const isLargeScreen = useMediaQuery(ScreenSizeQueries.LARGE);

    const { logMediaEvent } = useLogMediaEvents();

    const {
        data: cleDialogMessages,
    } = useGetCleDialogMessagesQuery();


    const dialogHeading = cleDialogMessages?.attendanceDialogHeader ?? 'Attendance verification required';
    const dialogBody = cleDialogMessages?.attendanceDialogBody ?? 'Verifying your attendance is necessary for receiving earned credits. Delayed verification may impact your credit.';
    const buttonText = cleDialogMessages?.attendanceDialogButton ?? 'Verify attendance';
    const successHeading = cleDialogMessages?.attendanceDialogSuccessHeader ?? 'Attendance verified';
    const successBody = cleDialogMessages?.attendanceDialogSuccessBody ?? 'Thanks for verifying your attendance. We’ll continue tracking your progress as you go.';
    const countdownDurationSeconds = configuredCountdownSeconds && configuredCountdownSeconds > 0
        ? configuredCountdownSeconds
        : undefined;

    const [isSoundEnabled, setIsSoundEnabled] = useState(true);

    const cleDialogRef = useRef<HTMLDivElement>(null);
    const verifyAttendanceButtonRef = useRef<HTMLButtonElement>(null);
    const { playerElement, refreshPlayerElement } = usePlayerElement();
    const previousActiveElement = useRef<HTMLElement>();

    const [saveTimeStamps, { isLoading, isSuccess, reset }] = useSaveCleTimeStampsMutation();
    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [isLate, setIsLate] = useState(false);
    const [isMenuExpanded, setIsMenuExpanded] = useState(false);
    const [acknowledgedProgress, setAcknowledgedProgress] = useState(0);

    const idleTimeoutRef = useRef<TimeoutHandle>();
    const acknowledgementTimeoutRef = useRef<TimeoutHandle>();
    const exitTransitionTimeoutRef = useRef<TimeoutHandle>();
    const urgencyTimeoutRef = useRef<TimeoutHandle>();
    const progressStartTimestampRef = useRef<number>();

    useEffect(() => {
        return () => {
            idleTimeoutRef.current && clearTimeout(idleTimeoutRef.current);
            acknowledgementTimeoutRef.current && clearTimeout(acknowledgementTimeoutRef.current);
            exitTransitionTimeoutRef.current && clearTimeout(exitTransitionTimeoutRef.current);
            urgencyTimeoutRef.current && clearTimeout(urgencyTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (!timeStampEvent) return;
        
        if (!document.body.contains(playerElement)) {
            refreshPlayerElement();
            // this will trigger the effect again since playerElement will change
            return;
        }

        // Prevents keyboard controls on CleDialog inputs from firing JWPlayer play/pause keyboard shortcuts
        const preventJwKeyEvent = (e: Event) => {
            const code = (e as KeyboardEvent).code;
            if (code === 'Space' || code === 'Enter' || code === 'ArrowUp' || code === 'ArrowDown') {
                e.stopPropagation();
            }
        };

        const jwPlayerControls = document.querySelectorAll('.jwplayer');

        setIsAcknowledged(false);
        setIsExiting(false);
        acknowledgementTimeoutRef.current && clearTimeout(acknowledgementTimeoutRef.current);
        exitTransitionTimeoutRef.current && clearTimeout(exitTransitionTimeoutRef.current);
        reset();

        jwPlayerControls.forEach(elem => {
            elem.addEventListener('keydown', preventJwKeyEvent, true);
        });

        // Don't render dialog over sidebar when sidebar is expanded
        const sidebar = document.getElementById('sidebar');
        setIsMenuExpanded(sidebar?.classList.contains('sidebar--expanded') ?? false);

        // Focus dialog
        previousActiveElement.current = document.activeElement as HTMLElement;
        cleDialogRef.current?.focus();

        if (isSoundEnabled && chime.current) {
            chime.current.load();
            void chime.current.play();
        }

        return () => {
            // Restore focus after dialog is closed
            if (!previousActiveElement) playerElement?.focus();
            previousActiveElement.current?.focus({ preventScroll: true });
            previousActiveElement.current = undefined;   

            jwPlayerControls.forEach(elem => {
                elem.removeEventListener('keydown', preventJwKeyEvent, true);
            });
        };
    // This effect should only happen when cleEvent is dispatched, or cleared
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeStampEvent, playerElement]);

    useEffect(() => {
        urgencyTimeoutRef.current && clearTimeout(urgencyTimeoutRef.current);
        urgencyTimeoutRef.current = undefined;

        if (!timeStampEvent) return;

        setIsLate(false);
        setAcknowledgedProgress(0);
        progressStartTimestampRef.current = countdownDurationSeconds ? Date.now() : undefined;

        if (countdownDurationSeconds) {
            urgencyTimeoutRef.current = setTimeout(() => {
                setIsLate(true);
                setAcknowledgedProgress(1);
            }, countdownDurationSeconds * 1000);
        }

        return () => {
            urgencyTimeoutRef.current && clearTimeout(urgencyTimeoutRef.current);
            urgencyTimeoutRef.current = undefined;
        };
    }, [countdownDurationSeconds, timeStampEvent]);

    useLayoutEffect(() => {
        // Move CPE dialog if CLE dialog is present
        const cpeDialog = document.getElementById('cpe-dialog');
        if (!cpeDialog) return;
        if (timeStampEvent || isAcknowledged)
            cpeDialog.classList.add('cpe-dialog--cle-dialog-open');
        else
            cpeDialog.classList.remove('cpe-dialog--cle-dialog-open');
    }, [isAcknowledged, timeStampEvent]);

    const handleVerifyAttendancePress = useCallback(async () => {
        if (timeStampEvent) {
            idleTimeoutRef.current && clearTimeout(idleTimeoutRef.current);
            idleTimeoutRef.current = undefined;
            urgencyTimeoutRef.current && clearTimeout(urgencyTimeoutRef.current);
            urgencyTimeoutRef.current = undefined;

            setAcknowledgedProgress(calculateAttendanceProgress(
                progressStartTimestampRef.current,
                Date.now(),
                countdownDurationSeconds,
            ));

            const now = new Date();
            const nowSeconds = Math.round(now.getTime() / 1000);
            const reactionSeconds = nowSeconds - timeStampEvent.displayDateSeconds;

            const cleTimeStamp : CleTimeStamp = {
                encryptedRegistrationID: timeStampEvent.registrationId,
                itemSK: timeStampEvent.itemSk,
                weight: timeStampEvent.weight,
                minWeight: timeStampEvent.minWeight,
                maxWeight: timeStampEvent.maxWeight,
                reactionSeconds: reactionSeconds,
                sessionGuid: sessionGuid,
                indexGuid: timeStampEvent.indexGuid,
                sequenceNo: timeStampEvent.sequence,
                isRecovered: false,
                reactionTimeStampUtc: nowSeconds,
            };

            // wait for concurrent timestamps sync
            await cleTimestampSaveLock.waitForUnlock();

            // prevent race condition by taking timestamps directly from store right before request
            const timestampsToSend = store.getState().cle.failedTimeStamps.map(ts => {
                const recoveredTs : CleTimeStamp = { ...ts, isRecovered: true };
                return recoveredTs;
            });
            timestampsToSend.push(cleTimeStamp);

            setIsAcknowledged(true);
            dispatch(clearTimeStampEvent());

            try {
                const response = await saveTimeStamps(timestampsToSend).unwrap();
                const failedTimeStamps = response.map(x => {
                    if (!x.isSuccess) {
                        return timestampsToSend.find(y => y.indexGuid === x.indexGuid);
                    }
                }).filter(x => x !== undefined) as CleTimeStamp[];
                dispatch(setFailedTimeStamps(failedTimeStamps));
            } catch (err) {
                logMediaEvent(MediaLogEventTypes.ERROR, `handleVerifyAttendancePress(): ${getErrorMessage(err)}`);
                dispatch(setFailedTimeStamps(timestampsToSend));
            }
            animateCreditIcon();
            acknowledgementTimeoutRef.current && clearTimeout(acknowledgementTimeoutRef.current);
            exitTransitionTimeoutRef.current && clearTimeout(exitTransitionTimeoutRef.current);

            exitTransitionTimeoutRef.current = setTimeout(() => {
                setIsExiting(true);
            }, Math.max(acknowledgementDisplayMilliseconds - exitTransitionMilliseconds, 0));
            acknowledgementTimeoutRef.current = setTimeout(() => {
                setIsAcknowledged(false);
                setIsExiting(false);
            }, acknowledgementDisplayMilliseconds);
        }
    }, [timeStampEvent, countdownDurationSeconds, sessionGuid, store, dispatch, animateCreditIcon, saveTimeStamps, logMediaEvent]);

    const shouldShowBackdropRef = useRef<boolean>(true);
    const largePanelOpenCommand = useAppSelector((state) => state.menu.largePanelOpenCommand);
    const handleJwControlsOverlay = () => {
        const jwControls = document.querySelectorAll('.jw-controls');
        const isOverlayOpen = document.querySelectorAll('.overlay-menu, .ReactModal__Overlay--after-open, .small-panel--opened').length > 0;
        const isAnyPanelOpen = document.querySelectorAll('.small-panel--opened, .large-panel--opened').length > 0;
        
        if (largePanelOpenCommand) {
            shouldShowBackdropRef.current = false;
        }

        jwControls.forEach((elem) => {
            elem.classList.add('zindex-dialog-backdrop');
        });

        const shouldNotBringJwControlsUp = !shouldShowBackdropRef.current || isOverlayOpen || (!isLargeScreen && isAnyPanelOpen);
        
        if (shouldNotBringJwControlsUp) {
            jwControls.forEach((elem) => {
                elem.classList.remove('zindex-dialog-backdrop');
            });
        }
    };

    const resetJwControlsOverlay = () => {
        const jwControls = document.querySelectorAll('.jw-controls');
        jwControls.forEach((elem) => {
            elem.classList.remove('zindex-dialog-backdrop');
        });
        shouldShowBackdropRef.current = true;
    };

    const progressStyle: CleProgressStyle = isAcknowledged
        ? { transform: `scaleX(${acknowledgedProgress})` }
        : countdownDurationSeconds
            ? { '--cle-dialog-progress-duration': `${countdownDurationSeconds}s` }
            : { transform: 'scaleX(0)' };

    const progressClass = [
        'cle-dialog__progress__value',
        countdownDurationSeconds && !isAcknowledged ? 'cle-dialog__progress__value--counting' : '',
        isLate ? 'cle-dialog__progress__value--late' : '',
        isSuccess ? 'cle-dialog__progress__value--verified' : '',
    ].filter(Boolean).join(' ');

    if (timeStampEvent || isAcknowledged) {
        handleJwControlsOverlay();

        const dialogContainerClass = [
            shouldShowBackdropRef.current ? 'cle-dialog__container' : 'cle-dialog__container-above',
            isExiting ? 'cle-dialog__container--fade-out' : '',
        ].filter(Boolean).join(' ');
        
        return createPortal(
            <div className={dialogContainerClass}>
                <div className={`cle-dialog ${isMenuExpanded ? 'cle-dialog--menu-expanded' : ''} ${isLate ? 'cle-dialog--late' : ''}`} id='cle-dialog' role='document' ref={cleDialogRef} tabIndex={-1}
                    aria-label='Attendance verification dialog'>
                    <div className='cle-dialog__content'>

                        <div className='cle-dialog__content__sound-setting'>
                            <label htmlFor='soundToggle' 
                                className='cle-dialog__content__sound-setting__label'>
                                <i className={`fa-kit fa-volume-${isSoundEnabled ? 'up' : 'mute'} cle-dialog__content__sound-setting__label__icon`}></i>
                                <span className='text--silver paragraph-2 cle-dialog__content__sound-setting__label__text'>
                                Notification Sounds
                                </span>
                            </label>
                            <Toggle id='soundToggle' 
                                checked={isSoundEnabled}
                                color={'amethyst'}
                                onChange={() => setIsSoundEnabled(state => !state)}
                                ariaLabel={`Turn ${isSoundEnabled ? 'off' : 'on'} notification sounds`}
                                cssClass='toggle-margin-ios'
                                isWithinPlayerPortal={true} />
                        </div>

                        <div className='cle-dialog__content__text' aria-live='polite'>
                            <h1 className='heading-5 mb-0'>{isSuccess ? successHeading : dialogHeading}</h1>
                            <p className='paragraph-2 text--silver'>
                                {isSuccess ? successBody : dialogBody}
                            </p>
                        </div>
                    </div>

                    <Button 
                        buttonClass={`cle-dialog__button sticky-button ${isSuccess ? 'sticky-button--success' : isLate ? 'cle-dialog__button--late' : ''}`}
                        label={isSuccess ? undefined : buttonText}
                        aria-label={isSuccess ? 'Attendance verified' : 'Verify attendance and close'}
                        action={handleVerifyAttendancePress}
                        isLoading={isLoading}
                        icon={isSuccess ? 'checkmark' : undefined}
                        iconPosition={isSuccess ? 'center' : undefined}
                        ref={verifyAttendanceButtonRef}
                        isPlayerElementButton={true}
                    />

                    <div className='cle-dialog__progress' aria-hidden={true}>
                        <div className={progressClass} style={progressStyle} />
                    </div>

                </div>
            </div>,
            playerElement ?? container?.current ?? document.body
        );
    } else {
        resetJwControlsOverlay();
        return <></>;
    }
};

export default CleDialog;
